import React, { FC, useMemo } from 'react';
import { Text } from '@cubeyou-team/hx-react-components';

import { getQueryPreviewPaletteColorForLevel } from 'src/modules/query-builder/constants';

type Segment =
  | { kind: 'text'; value: string }
  | { kind: 'op'; value: string; color: string };

/** Renders `boolean_string` verbatim; colors uppercase AND/OR/NOT using preview palette (paren nesting depth). */
function parseBooleanStringColored(input: string): Segment[] {
  const segments: Segment[] = [];
  let depth = 0;
  let i = 0;
  let buf = '';

  const flushText = () => {
    if (buf.length === 0) return;
    segments.push({ kind: 'text', value: buf });
    buf = '';
  };

  while (i < input.length) {
    const c = input[i];

    if (c === '(') {
      flushText();
      segments.push({ kind: 'text', value: '(' });
      depth += 1;
      i += 1;
    } else if (c === ')') {
      flushText();
      segments.push({ kind: 'text', value: ')' });
      depth = Math.max(0, depth - 1);
      i += 1;
    } else {
      const rest = input.slice(i);
      const opMatch = rest.match(/^(AND|OR|NOT)\b/);
      if (opMatch) {
        flushText();
        const op = opMatch[1];
        segments.push({
          kind: 'op',
          value: op,
          color: getQueryPreviewPaletteColorForLevel(depth),
        });
        i += op.length;
      } else {
        buf += c;
        i += 1;
      }
    }
  }

  flushText();
  return segments;
}

interface ColoredBooleanStringProps {
  text: string;
}

export const ColoredBooleanString: FC<ColoredBooleanStringProps> = ({
  text,
}) => {
  const segments = useMemo(() => parseBooleanStringColored(text), [text]);

  return (
    <div className="ml-1 min-w-0 flex-1">
      <Text
        tag="span"
        size="x_body"
        weight="regular"
        className="block w-full break-words whitespace-pre-wrap"
      >
        {segments.map((seg, idx) =>
          seg.kind === 'op' ? (
            <Text
              key={idx}
              tag="span"
              size="x_body"
              weight="regular"
              style={{ color: seg.color }}
            >
              {seg.value}
            </Text>
          ) : (
            <span key={idx}>{seg.value}</span>
          )
        )}
      </Text>
    </div>
  );
};
